import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { episodeService } from 'services/episodeService';
import {
  ClinicalEpisode,
  CloseEpisodioDto,
  CreateEpisodioDto,
  UpdateEpisodioDto,
} from 'types/models';

const KEY = 'episodes';

interface EpisodesAllParams {
  estado?: string;
  search?: string;
  desde?: string;
  hasta?: string;
}

interface PaginatedEpisodes {
  data: ClinicalEpisode[];
  meta: { total: number; page: number; limit: number; pages: number };
}

export function useEpisodesAll(params?: EpisodesAllParams) {
  return useQuery<PaginatedEpisodes, Error, ClinicalEpisode[]>({
    queryKey: [KEY, 'all', params],
    queryFn: () => episodeService.listAll(params),
    select: (res) => res.data,
  });
}

export function useEpisode(patientId: string, episodeId: string) {
  return useQuery<ClinicalEpisode>({
    queryKey: [KEY, 'one', patientId, episodeId],
    queryFn: () => episodeService.getById(patientId, episodeId),
    enabled: !!patientId && !!episodeId,
  });
}

export function useEpisodesByPatient(patientId: string) {
  return useQuery<PaginatedEpisodes, Error, ClinicalEpisode[]>({
    queryKey: [KEY, 'patient', patientId],
    queryFn: () => episodeService.listByPatient(patientId),
    enabled: !!patientId,
    select: (res) => res.data,
  });
}

export function useCreateEpisode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      patientId,
      payload,
    }: {
      patientId: string;
      payload: CreateEpisodioDto;
    }) => episodeService.create(patientId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'patient', data.pacienteId] });
    },
  });
}

export function useUpdateEpisode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      patientId,
      episodeId,
      payload,
    }: {
      patientId: string;
      episodeId: string;
      payload: UpdateEpisodioDto;
    }) => episodeService.update(patientId, episodeId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'patient', data.pacienteId] });
    },
  });
}

export function useCloseEpisode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      patientId,
      episodeId,
      payload,
    }: {
      patientId: string;
      episodeId: string;
      payload: CloseEpisodioDto;
    }) => episodeService.close(patientId, episodeId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'patient', data.pacienteId] });
    },
  });
}
